/**
 *  @swagger
 *  definitions:
 *    RegattaGroupParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        GroupId:
 *          description: ID of the associated Group
 *          type: string
 *        RatingType:
 *          description: Rating Type of the RegattaGroup
 *          $ref: '#/components/enums/RegattaGroupRatingType'
 *        EntriesQuantityLimit:
 *          description: Entries Quantity Limit of RegattaGroup
 *          type: integer
 *        AltName:
 *          description: Alternative Name of RegattaGroup
 *          type: string
 *        CoachEnabled:
 *          description: CoachEnabled of the RegattaGroup
 *          type: boolean
 *          type: string
 *        CrewQuantityLimit:
 *          description: Crew Quantity Limit of the RegattaGroup
 *          type: integer
 *        PreliminaryCrewQuantityEnabled:
 *          description: Preliminary Crew Quantity Enabled of the RegattaGroup
 *          type: boolean
 *
 *  @swagger
 *  definitions:
 *    RegattaGroupCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaGroupParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - GroupId
 *            - RatingType
 *            - CoachEnabled
 *            - PreliminaryCrewQuantityEnabled
 *
 *  @swagger
 *  definitions:
 *    RegattaGroupQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaGroup
 *              type: string
 *        - $ref: '#/definitions/RegattaGroupParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaGroupUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaGroupQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaGroup:
 *      allOf:
 *        - $ref: '#/definitions/RegattaGroupCreateParams'
 *        - $ref: '#/definitions/RegattaGroupUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaGroup:
 *        allOf:
 *          - $ref: '#/definitions/RegattaGroup'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaGroup
 *                $ref: '#/components/schemas/Regatta'
 *              Group:
 *                description: Group of the RegattaGroup
 *                $ref: '#/components/schemas/Group'
 *              RegattaEntries:
 *                description: RegattaEntries of the RegattaGroup
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaEntry'
 */

// Enums
import {
  RegattaGroupRatingType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  GroupId: string;
  RegattaId: string;
};

export type $Params = {
  AltName: null | string;
  CoachEnabled: boolean;
  CrewQuantityLimit: null | number;
  EntriesQuantityLimit: null | number;
  PreliminaryCrewQuantityEnabled: boolean;
  RatingType: RegattaGroupRatingType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Group?: $Model<'Group'>;
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  AltName: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  CoachEnabled: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  CrewQuantityLimit: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
    validate: {
      notEmpty: true,
    },
  },
  EntriesQuantityLimit: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  PreliminaryCrewQuantityEnabled: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  RatingType: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaGroupRatingType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaGroup'> => {
  const RegattaGroup = stack.define(
    'RegattaGroup',
    fields(Sequelize),
    {
      tableName: 'RegattaGroups',
    },
  );

  RegattaGroup.associate = (models: $ModelClasses) => {
    models.RegattaGroup.belongsTo(
      models.Group,
      {
        as: 'Group',
        foreignKey: 'GroupId',
      },
    );
    models.RegattaGroup.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaGroup.hasMany(
      models.RegattaEntry,
      {
        as: 'RegattaEntries',
        foreignKey: 'RegattaGroupId',
      },
    );
  };

  RegattaGroup.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaGroup;
});
